/** Mirrors `Qs` in newquestionaire.html (v2.1) — used to render stored answers. */
export const ASSESSMENT_QUESTIONS = [
  {
    id: 1,
    category: "Decision Architecture",
    question: "When a decision needs to be made and you’re not available, what does your leadership team do?",
    type: "choice",
    options: [
      "They decide within their defined authority and inform me afterward",
      "They make the call but second-guess it, and check with me when I’m back",
      "They wait. The decision sits until I’m available.",
      "They don’t have clear authority boundaries, they either freeze or guess"
    ]
  },
  {
    id: 2,
    category: "Decision Architecture",
    question: "Does your leadership team have explicitly defined decision rights, categories they can resolve without your involvement?",
    type: "choice",
    options: [
      "Yes, clear categories, clear thresholds, used confidently without checking",
      "Sort of, understood informally but not documented or consistently applied",
      "Not really, most decisions above a low threshold come to me for approval",
      "No. Decision rights have never been explicitly defined for this team."
    ]
  },
  {
    id: 3,
    category: "Decision Architecture",
    question: "When two leaders on your team disagree about how to handle something, what happens?",
    type: "choice",
    options: [
      "They resolve it between themselves based on defined role ownership",
      "One defers to the other, based on seniority or personality, not defined authority",
      "It escalates to me to make the call or break the tie",
      "It stays unresolved until I address it, or gets quietly dropped"
    ]
  },
  {
    id: 4,
    category: "Accountability Structure",
    question: "When you set a target or expectation with a leader on your team, how is progress tracked and communicated without you having to ask?",
    type: "choice",
    options: [
      "They proactively update me, flag risks early, and reset expectations before deadlines slip. I rarely have to ask.",
      "They update me when asked and are generally transparent, but I often need to prompt the conversation",
      "I find out about issues when they’ve already escalated, updates tend to come after the fact",
      "I’m often the last to know when something is off-track. By the time it reaches me, the damage is done."
    ]
  },
  {
    id: 5,
    category: "Accountability Structure",
    question: "A leader on your team missed a significant commitment last quarter. What happened next?",
    type: "choice",
    options: [
      "They owned it: named the miss, reset expectations, corrected it without me driving it",
      "We discussed it and agreed on changes, but real follow-through was inconsistent",
      "I had to intervene to resolve it, re-assign it, or absorb it myself",
      "It wasn’t formally addressed. The miss was absorbed without consequence."
    ]
  },
  {
    id: 6,
    category: "Accountability Structure",
    question: "What does your regular leadership meeting look like?",
    type: "choice",
    options: [
      "Each leader reports on their domain: what happened, what they decided, what’s next. I provide direction.",
      "Mixed, some updates, some problem-solving, some decisions come to me during the meeting itself",
      "I drive most of the agenda. Without my preparation and facilitation it doesn’t function well.",
      "We don’t have a consistent leadership meeting cadence."
    ]
  },
  {
    id: 7,
    category: "Founder Dependency",
    question: "When your business faces an unexpected challenge, a client escalation, a delivery failure, a team conflict, who resolves it?",
    type: "choice",
    options: [
      "My leadership team owns resolution completely. I find out when it’s done.",
      "They handle the operational side but loop me in on the final call",
      "They manage the symptoms but resolution usually requires my direct involvement",
      "I’m the first call, not the last resort. Escalation goes straight to me regardless of what it is."
    ]
  },
  {
    id: 8,
    category: "Founder Dependency",
    question: "Think about the work that occupied most of your time last week. How would you honestly categorise it?",
    type: "choice",
    options: [
      "Strategy, external relationships, and decisions only I should be making",
      "A mix, some strategic work, some operational problems I got pulled into",
      "Mostly operational, delivery issues, escalations, internal problems my team should have handled",
      "Hard to say. My week doesn’t separate cleanly. I’m in everything."
    ]
  },
  {
    id: 9,
    category: "Founder Dependency",
    question: "When your business faces a growth opportunity, a new client, an expansion decision, a strategic shift, who drives the evaluation?",
    type: "choice",
    options: [
      "My leadership team identifies it, evaluates it, and brings me a recommendation. I make the final call.",
      "We identify it together, but the evaluation and decision require my active involvement throughout",
      "I identify most growth opportunities. My team executes but doesn’t typically spot or drive them.",
      "My team doesn’t have enough context or authority to evaluate opportunities. It would always come to me."
    ]
  },
  {
    id: 10,
    category: "Margin Signal",
    question: "How has your margin trended over the last 12 to 18 months relative to your own expectations when you set this year’s targets?",
    type: "choice",
    options: [
      "Ahead or in line, margin is performing where I expected or better",
      "Slightly behind, smaller than expected, but I understand why",
      "Behind, compressing without a fully satisfying explanation",
      "Significantly behind or volatile. I can’t reliably predict or explain it quarter to quarter."
    ]
  },
  {
    id: 11,
    category: "Margin Signal",
    question: "When delivery quality or client satisfaction slips, what explanation does your leadership team give first?",
    type: "choice",
    options: [
      "A specific, named process or decision failure with a clear owner and a plan",
      "Workload or capacity, we were stretched thin",
      "External factors, client-side issues, market conditions, things outside our control",
      "It varies every time. No consistent diagnosis across similar problems."
    ]
  },
  {
    id: 12,
    category: "Margin Signal",
    question: "Can you draw a direct line between a specific decision or failure by someone on your leadership team and a margin outcome?",
    type: "choice",
    options: [
      "Yes, I can name specific decisions that affected margin in a specific way",
      "I have a strong suspicion but haven’t been able to confirm it with data",
      "Not really, margin feels like a separate outcome from what my leadership team does",
      "I’ve never approached it this way. Margin is tracked but not connected to leadership decisions."
    ]
  }
];
export function getRawAnswer(answers, questionId) {
  if (!answers || typeof answers !== "object") return undefined;
  const id = Number(questionId);
  return answers[questionId] ?? answers[id] ?? answers[String(id)];
}

export function formatAnswerDisplay(meta, raw) {
  if (raw === undefined || raw === null || raw === "") return "—";

  if (meta.type === "scale" || meta.type === "choice") {
    const n = Number(raw);
    if (!Number.isFinite(n)) return String(raw);
    const label = meta.options[n - 1];
    return label ?? String(n);
  }

  return String(raw);
}
